import { useLoadScript } from '@react-google-maps/api';
import { useEffect, useRef, useState } from 'react';
import { Input } from '../ui/input';

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (components: {
    address: string;
    city: string;
    state: string;
    postalCode: string;
  }) => void;
  className?: string;
  placeholder?: string;
}

const libraries: ('places')[] = ['places'];

export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  className,
  placeholder,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [inputValue, setInputValue] = useState(value);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (!isLoaded || !inputRef.current || autocompleteRef.current) return;

    const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'ng' },
      fields: ['address_components', 'formatted_address'],
      types: ['address'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place?.address_components) return;

      const components = place.address_components;
      let streetNumber = '';
      let route = '';
      let city = '';
      let state = '';
      let postalCode = '';

      components.forEach(component => {
        const types = component.types;

        if (types.includes('street_number')) {
          streetNumber = component.long_name;
        }
        if (types.includes('route')) {
          route = component.long_name;
        }
        if (
          types.includes('locality') ||
          types.includes('administrative_area_level_2')
        ) {
          city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          state = component.long_name;
        }
        if (types.includes('postal_code')) {
          postalCode = component.long_name;
        }
      });

      const fullAddress = [streetNumber, route].filter(Boolean).join(' ');

      setInputValue(fullAddress || place.formatted_address || '');
      onChange(fullAddress || place.formatted_address || '');

      onAddressSelect({
        address: fullAddress || place.formatted_address || '',
        city: city || '',
        state: state || '',
        postalCode: postalCode || '100001',
      });
    });

    autocompleteRef.current = autocomplete;

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isLoaded, onChange, onAddressSelect]);

  useEffect(() => {
    const stopPropagation = (e: Event) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
    };

    const preventSheetClose = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest('.pac-container')) {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    const observer = new MutationObserver(() => {
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach(container => {
        container.addEventListener('mousedown', stopPropagation, true);
        container.addEventListener('click', stopPropagation, true);
        container.addEventListener('touchstart', stopPropagation, true);
        container.addEventListener('touchend', stopPropagation, true);
      });
    });

    document.addEventListener('mousedown', preventSheetClose, true);
    document.addEventListener('click', preventSheetClose, true);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      .pac-container {
        z-index: 99999 !important;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3) !important;
        border-radius: 8px !important;
        margin-top: 4px !important;
        font-family: inherit !important;
        pointer-events: auto !important;
      }
      .pac-item {
        padding: 8px 12px !important;
        font-size: 14px !important;
        cursor: pointer !important;
        pointer-events: auto !important;
      }
      .pac-item:hover {
        background-color: #f3f4f6 !important;
      }
      .pac-item-query {
        font-size: 14px !important;
        pointer-events: auto !important;
      }
      .pac-matched {
        font-weight: 600 !important;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      observer.disconnect();
      document.head.removeChild(styleElement);
      document.removeEventListener('mousedown', preventSheetClose, true);
      document.removeEventListener('click', preventSheetClose, true);
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach(container => {
        container.removeEventListener('mousedown', stopPropagation, true);
        container.removeEventListener('click', stopPropagation, true);
        container.removeEventListener('touchstart', stopPropagation, true);
        container.removeEventListener('touchend', stopPropagation, true);
      });
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  if (loadError) {
    return (
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        className={className}
        placeholder={placeholder}
      />
    );
  }

  if (!isLoaded) {
    return (
      <Input
        disabled
        className={className}
        placeholder="Loading address search..."
      />
    );
  }

  return (
    <Input
      ref={inputRef}
      value={inputValue}
      onChange={handleInputChange}
      className={className}
      placeholder={placeholder}
      autoComplete="off"
    />
  );
}